import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Card,
  Button,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Table,
  Badge,
  InputGroup,
  Modal,
} from "react-bootstrap";
import api from "../../../axiosConfig";
import SignaturePad from "./SignaturePad";
import "./checksheet.css";

const MONTHS = [
  "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

const ChecksheetTab = () => {
  const now = new Date();
  const [machines, setMachines] = useState([]);
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState(""); // "" = semua sektor

  const [userSectorId, setUserSectorId] = useState(null);
  const [userSectorName, setUserSectorName] = useState("");
  const [userRole, setUserRole] = useState("");

  // Hak edit berdasarkan role
  const canEditDaily = userRole === "user" || userRole === "admin"; // operator isi harian/shift
  const canEditPrev = userRole === "maintenance" || userRole === "admin"; // maintenance isi total bln sblm

  const [view, setView] = useState("list"); // 'list' | 'grid'
  const [activeMachine, setActiveMachine] = useState(null);
  const [sheet, setSheet] = useState(null); // response GET (machine + tools)

  const [values, setValues] = useState({}); // { [standardId]: { [day]: val } }
  // Tanda tangan digital per peran: { operator, leader, mtc, spv } = data URL PNG
  const [signatures, setSignatures] = useState({});
  const [sigModal, setSigModal] = useState({ open: false, role: null, day: null });
  const [sigDraft, setSigDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const sheetRef = useRef(null);

  // Cetak lembar FM-3005: auto-scale (zoom) + regangkan tinggi tabel
  // agar isi MEMENUHI satu halaman A4 landscape (tanpa ruang kosong di bawah).
  const handlePrintSheet = () => {
    const el = sheetRef.current;
    if (!el) {
      window.print();
      return;
    }
    const mm = (v) => (v * 96) / 25.4; // mm -> px @96dpi
    const pageW = mm(297 - 12); // A4 landscape width - margin 6mm x2
    const pageH = mm(210 - 12); // A4 landscape height - margin 6mm x2

    const inner = el.querySelector(".cs-print-inner");
    const scroll = el.querySelector(".cs-scroll");
    const tableEl = el.querySelector(".cs-table");
    if (!inner) {
      window.print();
      return;
    }

    // Simpan style untuk dikembalikan setelah print.
    const prev = {
      elW: el.style.width,
      elH: el.style.height,
      elOv: el.style.overflow,
      inW: inner.style.width,
      inZoom: inner.style.zoom,
      scOv: scroll ? scroll.style.overflow : "",
    };

    // Reset ke ukuran natural agar pengukuran akurat.
    el.style.width = "";
    el.style.height = "";
    el.style.overflow = "";
    inner.style.width = "";
    inner.style.zoom = "";
    if (scroll) scroll.style.overflow = "visible";

    // PERBESAR FONT CETAK: pasang kelas .cs-print-boost SEBELUM mengukur, agar
    // teks (STANDAR/ITEM/label/footer) dengan kolom ber-lebar TETAP tampil lebih
    // besar. Karena kolom tak melebar (teks wrap), zoom tak ikut mengecil → teks
    // fisik benar-benar lebih besar. Diukur dalam kondisi ini → tak overflow.
    el.classList.add("cs-print-boost");

    // Ukur ukuran natural konten (membaca scrollWidth/Height memaksa reflow).
    const w = Math.max(inner.scrollWidth, tableEl ? tableEl.scrollWidth : 0) + 2;
    const h = inner.scrollHeight + 2;

    // ====== PAGINASI: MAKSIMAL 3 TOOL per HALAMAN A4 ======
    // Tiap halaman A4 = laporan utuh: <thead> (header format laporan) di atas +
    // <tfoot> (pemeriksa + catatan + kotak TTD) di bawah — keduanya OTOMATIS
    // BERULANG di tiap halaman cetak — dengan maksimal 3 tool di tengahnya.
    const TOOLS_PER_PAGE = 3;
    const thead = tableEl ? tableEl.querySelector("thead") : null;
    const footEl = el.querySelector(".cs-report-foot"); // pemeriksa + catatan + ttd
    const headH = thead ? thead.scrollHeight : 0;
    const footH = footEl ? footEl.scrollHeight : 0;
    // Tinggi baris No Dok & Revisi (cs-docmeta-foot) — ikut diperhitungkan agar
    // zoom pas & tiap halaman menyisakan ruang untuknya di dasar.
    const footDocEl = el.querySelector(".cs-docmeta-foot");
    const footDocH = footDocEl ? footDocEl.scrollHeight : 0;

    // Tiap tool = satu <tbody.cs-tool-group>. DIUKUR pada lebar NATURAL (kolom
    // tersempit = baris TERTINGGI); melebarkan kolom nanti hanya MEMENDEKKAN
    // baris → perhitungan ini konservatif/aman.
    const groupEls = Array.from(el.querySelectorAll(".cs-tool-group"));
    const toolHs = groupEls.map((g) => g.scrollHeight);

    // Jumlah halaman = ceil(jumlah tool / 3), minimal 1.
    const pages = Math.max(1, Math.ceil(groupEls.length / TOOLS_PER_PAGE));

    // Kelompok 3 tool PALING TINGGI menentukan zoom agar SEMUA halaman muat.
    let maxChunk = 0;
    for (let i = 0; i < toolHs.length; i += TOOLS_PER_PAGE) {
      let s = 0;
      for (let j = i; j < Math.min(i + TOOLS_PER_PAGE, toolHs.length); j++)
        s += toolHs[j];
      if (s > maxChunk) maxChunk = s;
    }
    // Tinggi 1 halaman = header + (maks 3 tool) + footer, semuanya di satu lembar.
    // MARGIN 5%: beri ruang lebih agar pembulatan/`break-inside:avoid` TIDAK
    // mendorong salinan footer ke halaman berikutnya (penyebab footer "hilang").
    const SAFETY = 1.05;
    const maxContent = (headH + maxChunk + footH + footDocH) * SAFETY || 1;

    // Zoom: paskan LEBAR A4, atau tinggi halaman-terpenuh (ambil yang terkecil).
    let z = Math.min(pageW / w, pageH / maxContent);
    if (!(z > 0)) z = 1;
    // ISI LEBAR A4: regangkan konten ke ~pageW. Disisakan EDGE px di kanan supaya
    // GARIS BORDER PALING KANAN tabel tidak jatuh TEPAT di tepi area cetak (yang
    // akan dipotong oleh batas halaman printer → border kanan hilang di kertas).
    // EDGE kecil (≈1.6mm) → tak terlihat sebagai perubahan layout.
    const EDGE = 6;
    const fillW = pageW - EDGE;
    inner.style.width = fillW / z + "px";

    // ===== FOOTER di SETIAP halaman =====
    // <tfoot> TIDAK andal berulang saat ada page-break paksa (Chrome hanya
    // menaruhnya sekali di akhir). Jadi footer dibiarkan sebagai 1 <tbody> di
    // akhir (= halaman terakhir), lalu SALINANNYA disisipkan setelah tiap 3 tool
    // (kecuali grup terakhir) dengan page-break-after → tiap halaman punya footer.
    // <thead> sudah berulang otomatis (header di tiap halaman).
    // Tiap halaman (kecuali terakhir) butuh SALINAN footer + SALINAN baris No Dok.
    // Urutan tiap halaman: [tools][footer][No Dok] lalu page-break. page-break
    // dipasang pada baris No Dok (paling bawah) → No Dok menutup tiap halaman di
    // LUAR border grid, sama seperti halaman terakhir.
    const insertedClones = [];
    if (groupEls.length > TOOLS_PER_PAGE && footEl) {
      for (
        let i = TOOLS_PER_PAGE - 1;
        i < groupEls.length - 1;
        i += TOOLS_PER_PAGE
      ) {
        const fClone = footEl.cloneNode(true);
        fClone.classList.add("cs-foot-clone");
        groupEls[i].after(fClone); // footer tepat sesudah tool ke-(i+1)
        insertedClones.push(fClone);

        let breakHost = fClone;
        if (footDocEl) {
          const dClone = footDocEl.cloneNode(true);
          dClone.classList.add("cs-foot-clone");
          fClone.after(dClone); // baris No Dok tepat di bawah footer
          insertedClones.push(dClone);
          breakHost = dClone; // page-break SETELAH No Dok
        }
        breakHost.style.pageBreakAfter = "always";
      }
    }

    // ===== KUNCI LEBAR KOLOM DETERMINISTIK + SNAP SEAM TANGGAL KE DEVICE-PIXEL =====
    // Penyebab garis vertikal antar tanggal HILANG saat cetak (mis. antara tgl
    // 14-15): kolom tanggal lebarnya PECAHAN (auto-layout), lalu di-zoom → batas
    // (seam) kolom jatuh di sub-pixel → border tipis di-render <1px → hilang.
    // Mematok lebar di <th> saja TIDAK cukup: di auto-layout, sel BODY kolom itu
    // tak dibatasi sehingga kolom masih bisa bergeser. Solusi tuntas: pasang
    // <colgroup> + table-layout:fixed → lebar kolom 100% ditentukan <col>, body
    // tak bisa menggesernya. Lebar tiap kolom = lebar SAAT INI (layout sama),
    // KECUALI kolom tanggal yang di-snap agar lebar SETELAH zoom = kelipatan 1
    // device-pixel (seam jatuh tepat di pixel → garis selalu tegas walau tipis).
    // Diukur SEBELUM zoom → getBoundingClientRect = px pra-zoom.
    let colgroupEl = null;
    const labelRow = tableEl
      ? tableEl.querySelector("thead tr:last-child")
      : null;
    if (labelRow && labelRow.children.length) {
      const headerCells = Array.from(labelRow.children);
      // ===== SNAP POSISI BATAS KOLOM KE DEVICE-PIXEL BULAT =====
      // Ukur POSISI batas kolom (left edge tiap sel + tepi kanan tabel), BUKAN
      // lebar. Alasan: di border-collapse, getBoundingClientRect().width tiap sel
      // MENGHITUNG GANDA border yang di-share → Σlebar > lebar tabel → saat
      // table-layout:fixed sisa selisih "nyasar" ke kolom terakhir → seam
      // 31↔TOTAL bergeser dari pixel & hilang. Posisi batas tidak kena ganda.
      // Tiap batas di-snap ke device-pixel bulat, lebar kolom = selisih batas /z
      // → SETIAP seam vertikal (termasuk 31↔TOTAL & tepi kanan) jatuh tepat di
      // pixel → tak ada garis hilang.
      const tableRect = tableEl.getBoundingClientRect();
      // Bangun LEFT-edge tiap GRID-column (bukan per <th>). Sel header ber-colSpan>1
      // (mis. "H/M/B" colSpan=2) MEWAKILI >1 kolom grid → harus menghasilkan >1
      // <col>. Tanpa ini, satu <col> selebar H/M/B jatuh ke sub-kolom "H/M" saja,
      // dan sub-kolom "peran" (Operator/Leader/MTC/SPV) kebagian lebar kolom
      // tanggal (~26px) → teks peran TERPOTONG saat cetak. Batas internal diukur
      // dari sel BODY sub-kolom (td.cs-role = tepi-kiri sub-kolom peran).
      const roleCell = tableEl.querySelector("td.cs-role");
      const bounds = [];
      headerCells.forEach((th) => {
        bounds.push(th.getBoundingClientRect().left - tableRect.left);
        const span = th.colSpan || 1;
        if (span > 1) {
          const left = th.getBoundingClientRect().left - tableRect.left;
          const next = th.nextElementSibling;
          const rightEdge = next
            ? next.getBoundingClientRect().left - tableRect.left
            : tableRect.width;
          if (th.classList.contains("cs-hmb-col") && roleCell) {
            // batas internal H/M ↔ peran = tepi-kiri sel peran (body)
            bounds.push(
              roleCell.getBoundingClientRect().left - tableRect.left,
            );
          } else {
            // fallback umum: bagi rata lebar span utk sub-kolom internal
            const step = (rightEdge - left) / span;
            for (let k = 1; k < span; k++) bounds.push(left + step * k);
          }
        }
      });
      bounds.push(tableRect.width); // tepi kanan kolom terakhir
      // Snap batas kolom ke DEVICE pixel (bukan sekadar CSS pixel): kalikan
      // devicePixelRatio sebelum di-round. Tanpa dpr, di layar ber-skala
      // (125%/150% → dpr 1.25/1.5) seam jatuh di setengah device-pixel →
      // garis vertikal (30↔31, 31↔TOTAL, dst.) bisa HILANG saat zoom preview.
      const dpr = window.devicePixelRatio || 1;
      const zd = z * dpr;
      const snapped = [];
      let prevDev = Math.round(bounds[0] * zd);
      for (let i = 1; i < bounds.length; i++) {
        const dev = Math.round(bounds[i] * zd);
        snapped.push((dev - prevDev) / zd); // ×(z·dpr)=bulat → seam di device px
        prevDev = dev;
      }
      const cg = document.createElement("colgroup");
      snapped.forEach((w) => {
        const c = document.createElement("col");
        c.style.width = Math.max(0, w).toFixed(3) + "px";
        cg.appendChild(c);
      });
      tableEl.insertBefore(cg, tableEl.firstChild);
      tableEl.style.tableLayout = "fixed";
      colgroupEl = cg;
    }

    // ===== REGANGKAN TINGGI TOOL agar TIAP HALAMAN PENUH A4 =====
    // Zoom (z) dihitung dari halaman PALING PADAT (maks 3 tool). Akibatnya halaman
    // dgn sedikit tool (mis. halaman terakhir berisi 1 tool, atau mesin dgn 1-2
    // tool) mengisi LEBAR A4 tapi menyisakan ruang kosong di BAWAH → tampak kecil.
    // Di sini, untuk SETIAP halaman, tinggi baris tiap tool diregangkan proporsional
    // agar isi halaman memenuhi tinggi A4. Dilakukan SEBELUM snap device-pixel di
    // bawah, sehingga border tetap tajam. Overhead per halaman = header (berulang
    // otomatis) + footer + baris No Dok, jadi tak akan mendorong isi ke halaman
    // berikutnya (FILL < 1 menyisakan sedikit ruang pengaman).
    if (groupEls.length) {
      const availPerPage = pageH / z; // tinggi pre-zoom yg memetakan 1 lembar A4
      const overheadH = headH + footH + footDocH; // konsumsi tetap tiap halaman
      const FILL = 0.985; // sisakan ~1.5% agar tak tumpah ke halaman berikutnya
      for (let p = 0; p < pages; p++) {
        const pageGroups = groupEls.filter(
          (_, idx) => Math.floor(idx / TOOLS_PER_PAGE) === p,
        );
        if (!pageGroups.length) continue;
        const contentH = pageGroups.reduce((s, g) => s + g.scrollHeight, 0);
        if (contentH <= 0) continue;
        const availForTools = (availPerPage - overheadH) * FILL;
        const factor = availForTools / contentH;
        if (!(factor > 1)) continue; // sudah penuh/lebih → jangan dikecilkan
        pageGroups.forEach((g) => {
          Array.from(g.querySelectorAll("tr")).forEach((tr) => {
            const hNow = tr.getBoundingClientRect().height; // pre-zoom
            tr.style.height = (hNow * factor).toFixed(3) + "px";
          });
        });
      }
    }

    // ===== SNAP TINGGI BARIS ke DEVICE-pixel (samakan tebal garis HORIZONTAL) =====
    // Sama prinsipnya dgn snap kolom: ukur batas-atas tiap baris (pre-zoom, SETELAH
    // colgroup/fixed di atas + regang tinggi → tinggi sudah final), bulatkan ke
    // device-pixel, lalu set tinggi baris = selisih batas. Tiap garis horizontal
    // jatuh tepat di device-pixel → tebalnya seragam (tak ada lagi tebal-tipis).
    let snappedRows = null;
    if (tableEl) {
      const allRows = Array.from(tableEl.querySelectorAll("tr"));
      if (allRows.length) {
        const tRect = tableEl.getBoundingClientRect();
        const rTops = allRows.map(
          (tr) => tr.getBoundingClientRect().top - tRect.top,
        );
        rTops.push(tRect.height); // batas bawah baris terakhir = tinggi tabel
        const zdR = z * (window.devicePixelRatio || 1);
        let prevD = Math.round(rTops[0] * zdR);
        for (let i = 0; i < allRows.length; i++) {
          const nextD = Math.round(rTops[i + 1] * zdR);
          allRows[i].style.height =
            Math.max(0, (nextD - prevD) / zdR).toFixed(3) + "px";
          prevD = nextD;
        }
        snappedRows = allRows;
      }
    }

    // ===== SNAP posisi 3 GARIS TULIS (background) kotak Permasalahan/Penyelesaian =====
    // Garis dibuat dari background-gradient di 25/50/75% tinggi kotak → posisinya
    // bisa jatuh di sub-pixel → tebal garis tak rata. Hitung posisi tiap garis dari
    // tinggi kotak (SETELAH row-snap), snap ke device-pixel, set via inline
    // !important (menang atas CSS .cs-foot-lines yg ber-!important).
    let snappedFootLines = null;
    {
      const zdL = z * (window.devicePixelRatio || 1);
      const fls = Array.from(
        (tableEl || document).querySelectorAll(".cs-foot-lines"),
      );
      fls.forEach((fl) => {
        const h = fl.getBoundingClientRect().height; // pre-zoom
        const pos = [0.25, 0.5, 0.75]
          .map((f) => "0 " + (Math.round(h * f * zdL) / zdL).toFixed(3) + "px")
          .join(", ");
        fl.style.setProperty("background-position", pos, "important");
      });
      if (fls.length) snappedFootLines = fls;
    }

    // Terapkan. Kotak halaman (el) dipaku TEPAT N halaman + overflow hidden
    // sebagai JARING PENGAMAN → konten tak pernah tumpah ke halaman N+1.
    inner.style.zoom = String(z);
    el.style.width = pageW + "px";
    el.style.height = pages * pageH + "px";
    // Klip HANYA arah VERTIKAL (jaring pengaman anti tumpah ke halaman N+1).
    // Arah horizontal dibiarkan visible supaya GARIS BORDER PALING KANAN tabel
    // (tepat di tepi pageW) TIDAK ikut ter-potong klip. (overflow-y: clip
    // membolehkan overflow-x tetap visible, beda dgn hidden.)
    el.style.overflowX = "visible";
    el.style.overflowY = "clip";
    // Lebar border SERAGAM: dipaku ke KELIPATAN BULAT device-pixel. Tanpa ini,
    // "1.3/z px" setelah di-zoom + devicePixelRatio jadi pecahan device-px → ada
    // border dibulatkan ke 1px, ada ke 2px (tampak tebal-tipis tak rata). Dengan
    // membulatkan tebal ke N device-px lalu dibagi (z·dpr), SEMUA garis = N
    // device-px persis → seragam.
    // tebal border (device-px) = pbw_cssPreZoom * z * dpr. Dgn pbw lama = 1.3/z,
    // tebalnya = 1.3*dpr → bulatkan ke N device-px, lalu pbw = N/(z*dpr).
    // PENTING: border-collapse MEMUSATKAN garis di batas sel (±N/2). Agar tepi
    // border jatuh di device-pixel BULAT (batas sudah di-snap ke pixel bulat),
    // N harus GENAP → tepi di P±(N/2) tetap bulat → garis tajam & SERAGAM.
    // N ganjil → tepi di P±0,5 → anti-alias → tebal-tipis tak rata.
    // Tebal border STANDAR (tipis, ~1 device-px). Sebelumnya dipaksa min 2 &
    // GENAP demi garis super-tajam, tapi hasilnya terlalu tebal. Sekarang pakai
    // ~1 device-px (min 1) → garis tipis standar, tetap konsisten (tebal border
    // device-px = pbw * z * dpr = pbwDev untuk semua garis).
    const pbwDpr = window.devicePixelRatio || 1;
    const pbwDev = Math.max(1, Math.round(pbwDpr)); // tebal dlm device-px (tipis)
    el.style.setProperty("--pbw", (pbwDev / (z * pbwDpr)).toFixed(3) + "px");

    const cleanup = () => {
      el.style.width = prev.elW;
      el.style.height = prev.elH;
      el.style.overflow = prev.elOv;
      el.style.overflowX = "";
      el.style.overflowY = "";
      inner.style.width = prev.inW;
      inner.style.zoom = prev.inZoom;
      if (scroll) scroll.style.overflow = prev.scOv;
      el.style.removeProperty("--pbw");
      // buang colgroup + kembalikan table-layout & width ke semula
      if (colgroupEl && colgroupEl.parentNode) colgroupEl.remove();
      if (snappedRows) snappedRows.forEach((tr) => (tr.style.height = ""));
      if (snappedFootLines)
        snappedFootLines.forEach((fl) =>
          fl.style.removeProperty("background-position"),
        );
      if (tableEl) {
        tableEl.style.tableLayout = "";
        tableEl.style.width = "";
      }
      el.classList.remove("cs-print-boost");
      insertedClones.forEach((c) => c.remove());
      window.removeEventListener("afterprint", cleanup);
    };
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => window.print(), 120);
  };

  const daysInMonth = useMemo(
    () => new Date(year, month, 0).getDate(),
    [year, month],
  );
  const dayList = useMemo(
    () => Array.from({ length: daysInMonth }, (_, i) => i + 1),
    [daysInMonth],
  );

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get("/auth/me");
        const u = meRes.data?.user || {};
        setUserSectorId(u.sector_id ?? null);
        setUserSectorName(u.sector_name || "");
        setUserRole(u.role || "");
      } catch (_) {
        /* abaikan */
      }
      try {
        const res = await api.get("/machines");
        setMachines(res.data || []);
      } catch (e) {
        setMsg({ type: "danger", text: "Gagal memuat daftar mesin" });
      }
    })();
  }, []);

  // Daftar mesin setelah dibatasi sektor milik user (basis utk opsi sektor).
  const sectorBaseMachines = useMemo(() => {
    if (!userSectorId) return machines;
    return machines.filter(
      (m) => Number(m.sector?.id) === Number(userSectorId),
    );
  }, [machines, userSectorId]);

  // Opsi sektor (unik) dari basis mesin yg boleh dilihat user.
  const sectorOptions = useMemo(() => {
    const map = new Map();
    sectorBaseMachines.forEach((m) => {
      const s = m.sector;
      if (s?.id != null && !map.has(s.id)) map.set(s.id, s.name || "-");
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) =>
      String(a.name).localeCompare(String(b.name)),
    );
  }, [sectorBaseMachines]);

  const visibleMachines = useMemo(() => {
    let list = sectorBaseMachines;
    if (sectorFilter) {
      list = list.filter((m) => String(m.sector?.id) === String(sectorFilter));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) =>
          m.name?.toLowerCase().includes(q) ||
          m.code?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [sectorBaseMachines, sectorFilter, search]);

  const openGrid = async (machine) => {
    setActiveMachine(machine);
    setView("grid");
    setLoading(true);
    setMsg(null);
    try {
      const res = await api.get(
        `/checksheet-entry/${machine.id}?year=${year}&month=${month}`,
      );
      setSheet(res.data);
      const v = {};
      (res.data.tools || []).forEach((tool) =>
        tool.standards.forEach((s) => {
          if (s.type === "QTY") {
            v[s.id] = {
              shift1: { ...(s.days?.shift1 || {}) },
              shift2: { ...(s.days?.shift2 || {}) },
              prev: s.days?.prev ?? "",
            };
          } else {
            v[s.id] = { ...(s.days || {}) };
          }
        }),
      );
      setValues(v);
      setSignatures(res.data.signatures || {});
    } catch (e) {
      setMsg({ type: "danger", text: "Gagal memuat check sheet" });
      setSheet(null);
      setValues({});
      setSignatures({});
    } finally {
      setLoading(false);
    }
  };

  const backToList = () => {
    setView("list");
    setActiveMachine(null);
    setSheet(null);
    setValues({});
    setSignatures({});
    setMsg(null);
  };

  // ===== Tanda tangan digital PER TANGGAL (modal gambar) =====
  const SIGN_ROLES = { operator: "Operator", leader: "Leader", mtc: "MTC", spv: "SPV" };
  const openSig = (role, day) => {
    setSigDraft(signatures[role]?.[day] || "");
    setSigModal({ open: true, role, day });
  };
  const closeSig = () => setSigModal({ open: false, role: null, day: null });
  const applySig = () => {
    const { role, day } = sigModal;
    setSignatures((prev) => {
      const roleMap = { ...(prev[role] || {}) };
      if (sigDraft) roleMap[day] = sigDraft;
      else delete roleMap[day];
      return { ...prev, [role]: roleMap };
    });
    closeSig();
  };

  // Sel TTD mengikuti tiap KOLOM TANGGAL untuk 1 peran, + kolom TOTAL kosong.
  // Klik sebuah sel tanggal → buka modal gambar TTD utk tanggal itu.
  const signDayCells = (role) => (
    <>
      {dayList.map((d) => (
        <td
          key={d}
          className="cs-pem-day cs-sign-day"
          onClick={() => openSig(role, d)}
          title={`Tanda tangan tgl ${d}`}
        >
          {signatures[role]?.[d] ? (
            <img className="cs-sign-day-img" src={signatures[role][d]} alt="" />
          ) : null}
        </td>
      ))}
      <td className="cs-pem-day" />
    </>
  );

  const setCell = (stdId, day, val) => {
    setValues((prev) => ({
      ...prev,
      [stdId]: { ...(prev[stdId] || {}), [day]: val },
    }));
  };

  // QTY: set nilai shift (shift1/shift2) untuk hari tertentu
  const setShiftCell = (stdId, shift, day, val) => {
    setValues((prev) => {
      const cur = prev[stdId] || {};
      return {
        ...prev,
        [stdId]: {
          ...cur,
          [shift]: { ...(cur[shift] || {}), [day]: val },
        },
      };
    });
  };

  // Hitung akumulatif QTY per hari: prev + Σ(shift1+shift2) s/d hari itu
  const computeAkumulatif = (stdId) => {
    const cur = values[stdId] || {};
    const prevNum = parseFloat(cur.prev) || 0;
    const s1 = cur.shift1 || {};
    const s2 = cur.shift2 || {};
    const out = {};
    let run = prevNum;
    for (const d of dayList) {
      const a = parseFloat(s1[d]) || 0;
      const b = parseFloat(s2[d]) || 0;
      run += a + b;
      out[d] = run;
    }
    return out;
  };

  const handleSave = async () => {
    if (!activeMachine) return;
    setSaving(true);
    setMsg(null);
    try {
      const entries = [];
      (sheet?.tools || []).forEach((tool) =>
        tool.standards.forEach((s) => {
          entries.push({ standard_id: s.id, days: values[s.id] || {} });
        }),
      );
      await api.put(`/checksheet-entry/${activeMachine.id}`, {
        year,
        month,
        entries,
        signatures,
      });
      setView("list");
      setActiveMachine(null);
      setSheet(null);
      setValues({});
      setSignatures({});
      setMsg({ type: "success", text: "Data berhasil disimpan" });
      // Auto-hilangkan notifikasi sukses setelah beberapa detik.
      setTimeout(() => setMsg(null), 3500);
    } catch (e) {
      setMsg({
        type: "danger",
        text: e.response?.data?.message || "Gagal menyimpan data",
      });
    } finally {
      setSaving(false);
    }
  };

  // Notifikasi mengambang (selalu terlihat, apa pun posisi scroll / view).
  const msgBanner = msg ? (
    <div
      style={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1080,
        minWidth: 320,
        maxWidth: "90%",
      }}
    >
      <Alert
        variant={msg.type}
        onClose={() => setMsg(null)}
        dismissible
        className="shadow mb-0"
      >
        {msg.text}
      </Alert>
    </div>
  ) : null;

  // ===================== LIST VIEW =====================
  if (view === "list") {
    return (
      <div className="cs-list-page" style={{ padding: 10 }}>
        <Card>
          <Card.Header
            style={{ background: "#1e3a8a", color: "#fff", fontWeight: 600 }}
          >
            Check Sheet Harian
            {userSectorName && ` — Sektor ${userSectorName}`}
          </Card.Header>
          <Card.Body>
            {msgBanner}
            <Row className="g-2 align-items-end mb-3">
              <Col md={5}>
                <Form.Label>Sektor</Form.Label>
                <Form.Select
                  value={sectorFilter}
                  onChange={(e) => setSectorFilter(e.target.value)}
                >
                  <option value="">Semua Sektor</option>
                  {sectorOptions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={7}>
                <Form.Label>Cari Mesin</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="nama / kode..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>

            <Table striped bordered hover responsive className="align-middle">
              <thead>
                <tr style={{ background: "#1e3a8a", color: "#fff" }}>
                  <th style={{ width: 50 }}>No</th>
                  <th style={{ width: 120 }}>Kode</th>
                  <th>Nama Mesin</th>
                  <th style={{ width: 160 }}>Sektor</th>
                  <th style={{ width: 100 }}>Jml Tools</th>
                  <th style={{ width: 160 }}>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {visibleMachines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted">
                      Tidak ada mesin di sektor Anda
                    </td>
                  </tr>
                ) : (
                  visibleMachines.map((m, i) => (
                    <tr key={m.id}>
                      <td className="text-center">{i + 1}</td>
                      <td>{m.code || "-"}</td>
                      <td className="cell-left">{m.name || "-"}</td>
                      <td className="cell-left">{m.sector?.name || "-"}</td>
                      <td className="text-center">
                        <Badge bg={m.tools?.length ? "success" : "secondary"}>
                          {m.tools?.length || 0}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openGrid(m)}
                        >
                          Isi Check Sheet
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <small className="text-muted">
              Periode: <strong>{MONTHS[month - 1]} {year}</strong>. Klik "Isi
              Check Sheet" untuk mulai.
            </small>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // ===================== GRID VIEW (format FM-3005) =====================
  const tools = sheet?.tools || [];

  return (
    <div style={{ padding: 10 }}>
      <div className="d-flex justify-content-between mb-2">
        <Button variant="secondary" size="sm" onClick={backToList}>
          ← Kembali
        </Button>
        <div className="d-flex gap-2">
          {userRole === "maintenance" && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handlePrintSheet}
              disabled={loading || tools.length === 0}
            >
              🖨️ Print
            </Button>
          )}
          <Button
            variant="success"
            size="sm"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      </div>

      {msgBanner}

      {loading ? (
        <div className="text-center p-4">
          <Spinner animation="border" /> Memuat...
        </div>
      ) : tools.length === 0 ? (
        <Alert variant="info">
          Mesin ini belum punya tools/standar. Minta admin mengisi di menu
          "Check Sheet Mesin" dulu.
        </Alert>
      ) : (
        <div className="cs-sheet" ref={sheetRef}>
          <div className="cs-print-inner">
          {/* ===== TABEL (header FM-3005 jadi bagian tabel agar border lurus) ===== */}
          <div className="cs-scroll">
          <table className="cs-table">
            <thead>
              {/* Baris 1: satu sel SELEBAR HALAMAN → judul BENAR-BENAR di tengah;
                  brand dipaku kiri, Bulan/Tahun dipaku kanan. Di dalam <thead>
                  agar BERULANG di tiap halaman cetak. */}
              <tr className="cs-hd-band">
                <th colSpan={8 + dayList.length} className="cs-hd-title">
                  <div className="cs-hd-title-row">
                    <span className="cs-hd-brand-inline">PT. CABLE TECH</span>
                    <span className="cs-hd-title-text">
                      Laporan Pemeriksaan Jig / Dies &amp; Mould
                      <span className="cs-hd-title-cn">設備保養</span>
                    </span>
                    <span className="cs-hd-period">
                      BULAN : <b>{MONTHS[month - 1]}</b>
                      &nbsp;&nbsp;&nbsp; TAHUN : <b>{year}</b>
                    </span>
                  </div>
                </th>
              </tr>
              {/* Baris 2: LINE | sektor | NAMA M/C | legenda H/M/B */}
              <tr className="cs-hd-band">
                <th colSpan={1} className="cs-hd-info-lbl">
                  LINE
                </th>
                <th colSpan={2} className="cs-hd-info-val">
                  {sheet?.machine?.sector_name || "-"}
                </th>
                <th colSpan={1} className="cs-hd-namamc">
                  NAMA M/C
                </th>
                <th colSpan={4 + dayList.length} className="cs-hd-freq">
                  <div className="cs-hd-freq-inner">
                    <span className="cs-hd-mcname">
                      {sheet?.machine?.name || "-"}
                    </span>
                    {sheet?.machine?.code ? (
                      <span className="cs-hd-mcno">
                        NO : {sheet.machine.code}
                      </span>
                    ) : null}
                    <span className="cs-hd-freq-legend">
                      <span>
                        <b>H</b> HARIAN
                      </span>
                      <span>
                        <b>M</b> MINGGUAN
                      </span>
                      <span>
                        <b>B</b> BULANAN
                      </span>
                    </span>
                  </div>
                </th>
              </tr>
              {/* Baris label kolom */}
              <tr>
                <th className="cs-foto-col" style={{ minWidth: 162 }}>
                  FOTO
                </th>
                <th style={{ minWidth: 36 }}>NO</th>
                <th className="cs-kode-col">ITEM CHECK / KODE</th>
                <th className="cs-std-col" style={{ minWidth: 240 }}>
                  STANDAR
                </th>
                <th className="cs-prev-col" style={{ minWidth: 78 }}>
                  TOTAL BLN
                  <br />
                  SEBELUMNYA
                </th>
                <th className="cs-hmb-col" colSpan={2} style={{ minWidth: 52 }}>
                  H/M/B
                </th>
                {dayList.map((d) => (
                  <th key={d} className="cs-daycol">
                    {d}
                  </th>
                ))}
                <th style={{ minWidth: 48 }} className="cs-total-col">
                  TOTAL
                </th>
              </tr>
            </thead>
              {tools.map((tool, ti) => {
                const stds = tool.standards || [];
                if (stds.length === 0) {
                  return (
                    <tbody
                      className="cs-tool-group"
                      key={tool.machine_tool_id}
                    >
                      <tr>
                        <td></td>
                        <td className="text-center">{ti + 1}</td>
                        <td>
                          <strong>{tool.kode || tool.part_name}</strong>
                          {tool.dimensi ? (
                            <div className="text-muted">{tool.dimensi}</div>
                          ) : null}
                        </td>
                        <td
                          colSpan={5 + dayList.length}
                          className="text-muted text-center"
                        >
                          Belum ada standar untuk tool ini
                        </td>
                      </tr>
                    </tbody>
                  );
                }

                // helper tipe QTY: "QTY" lama (gabung 2 baris) + split "QTY1"/"QTY2"
                const isQtyType = (t) =>
                  t === "QTY" || t === "QTY1" || t === "QTY2";

                // total baris yg dirender ("QTY" gabung = 2 baris; QTY1/QTY2/lain = 1)
                const toolRowSpan = stds.reduce(
                  (a, s) => a + (s.type === "QTY" ? 2 : 1),
                  0,
                );

                // Akumulatif tool = seed (maintenance) + Σ(semua baris QTY shift) berjalan
                const computeToolAkum = (seed) => {
                  const qtyStds = stds.filter((x) => isQtyType(x.type));
                  const out = {};
                  let run = seed || 0;
                  for (const d of dayList) {
                    let sum = 0;
                    for (const q of qtyStds) {
                      const v = values[q.id] || {};
                      if (q.type === "QTY") {
                        // gabung lama: shift1 + shift2
                        sum +=
                          (parseFloat(v.shift1?.[d]) || 0) +
                          (parseFloat(v.shift2?.[d]) || 0);
                      } else {
                        // split single-shift (QTY1/QTY2): nilai harian langsung
                        sum += parseFloat(v[d]) || 0;
                      }
                    }
                    run += sum;
                    out[d] = run;
                  }
                  return out;
                };

                // ===== Kolom TOTAL BLN SEBELUMNYA =====
                // Baris checklist + QTY -> 1 sel gabungan (berisi input Total QTY bln sblm).
                // Standar STROKE -> 1 sel sendiri (input Total stroke bln sblm).
                //
                // PENTING: JANGAN berasumsi STROKE selalu di baris paling bawah.
                // Standar bisa berurutan bebas (mis. BIASA, QTY, STROKE, BIASA).
                // Kalau sel gabung dibuat rowSpan = "semua baris - baris stroke"
                // dari baris pertama, ia bisa MENIMPA baris STROKE dan membuat
                // baris setelahnya kekurangan sel -> kolom tanggal tergeser &
                // nilai TOTAL jatuh ke kolom yang salah.
                //
                // Solusi: pecah standar jadi RUN kontigu non-STROKE. Tiap run =
                // 1 sel gabung (isi = prev QTY bila run berisi QTY, selain itu
                // kosong). Tiap STROKE = sel sendiri. Rencana disimpan per-index
                // standar: {rowSpan, kind} atau null (tercakup sel di atasnya).
                const rowCountOf = (s) => (s.type === "QTY" ? 2 : 1);
                const totalBlnPlan = {};
                {
                  let i = 0;
                  while (i < stds.length) {
                    if ((stds[i].type || "BIASA") === "STROKE") {
                      totalBlnPlan[i] = { rowSpan: 1, kind: "stroke", std: stds[i] };
                      i += 1;
                    } else {
                      let j = i;
                      let span = 0;
                      let qtyInRun = null;
                      while (
                        j < stds.length &&
                        (stds[j].type || "BIASA") !== "STROKE"
                      ) {
                        span += rowCountOf(stds[j]);
                        if (isQtyType(stds[j].type) && !qtyInRun)
                          qtyInRun = stds[j];
                        j += 1;
                      }
                      totalBlnPlan[i] = { rowSpan: span, kind: "merge", std: qtyInRun };
                      for (let k = i + 1; k < j; k += 1) totalBlnPlan[k] = null;
                      i = j;
                    }
                  }
                }

                const rows = [];
                let firstToolRowDone = false;

                // satu sel input "total bln sebelumnya" (dipakai QTY & STROKE)
                const totalBlnInput = (s, rowSpan, title) => (
                  <td
                    rowSpan={rowSpan}
                    className="align-middle cs-prev-cell"
                    style={{ padding: 0, textAlign: "center" }}
                  >
                    {canEditPrev ? (
                      <input
                        className="cs-input cs-input-prev"
                        value={values[s.id]?.prev ?? ""}
                        onChange={(e) => setCell(s.id, "prev", e.target.value)}
                        title={title}
                      />
                    ) : (
                      <span style={{ fontWeight: 700, color: "#1e3a8a" }}>
                        {values[s.id]?.prev ?? ""}
                      </span>
                    )}
                  </td>
                );

                // sel TOTAL BLN utk baris PERTAMA tiap standar (null = tercakup merge)
                const totalBlnFor = (s, si) => {
                  const plan = totalBlnPlan[si];
                  if (!plan) return null; // tercakup sel gabung di atasnya
                  // STROKE -> sel sendiri
                  if (plan.kind === "stroke")
                    return totalBlnInput(
                      plan.std,
                      plan.rowSpan,
                      "Nilai standar stroke (patokan) — akumulatif >= nilai ini jadi merah",
                    );
                  // run non-STROKE -> 1 sel gabung (isi prev QTY bila ada)
                  return plan.std
                    ? totalBlnInput(
                        plan.std,
                        plan.rowSpan,
                        "Total QTY bulan sebelumnya",
                      )
                    : <td rowSpan={plan.rowSpan} className="cs-prev-cell"></td>;
                };

                // FOTO: SATU sel & SATU foto per TOOL (ITEM CHECK / KODE), digabung
                // sepanjang seluruh baris tool (rowSpan = toolRowSpan) — mengikuti
                // KODE. Fotonya diambil dari DATA TOOL (tool.photo) yang di-upload di
                // halaman Tools, BUKAN dari standar. Kalau tak ada foto → KOSONG.
                const toolFotoCell = () => (
                  <td rowSpan={toolRowSpan} className="cs-foto">
                    {tool.photo ? (
                      <div className="cs-foto-item">
                        <img
                          src={tool.photo}
                          alt={`foto ${tool.kode || tool.part_name || ""}`}
                        />
                      </div>
                    ) : null}
                  </td>
                );

                const noKodeCells = () => (
                  <>
                    <td
                      rowSpan={toolRowSpan}
                      className="text-center align-middle"
                      style={{ fontWeight: 700, color: "#1e3a8a" }}
                    >
                      {ti + 1}
                    </td>
                    <td rowSpan={toolRowSpan} className="align-middle cs-kode">
                      <strong>{tool.kode || tool.part_name}</strong>
                      {tool.dimensi ? (
                        <div className="text-muted cs-kode-dim">
                          {tool.dimensi}
                        </div>
                      ) : null}
                    </td>
                  </>
                );

                const prevCell = (s, rowSpan) => (
                  <td rowSpan={rowSpan} className="cs-daycol" style={{ padding: 0 }}>
                    <input
                      className="cs-input cs-input-prev"
                      value={values[s.id]?.prev ?? ""}
                      onChange={(e) => setCell(s.id, "prev", e.target.value)}
                      readOnly={!canEditPrev}
                      title={canEditPrev ? "" : "Diisi oleh Maintenance"}
                    />
                  </td>
                );

                stds.forEach((s, si) => {
                  if (s.type === "QTY") {
                    // baris 1: SHIFT 1
                    rows.push(
                      <tr key={`${s.id}-s1`}>
                        {!firstToolRowDone && toolFotoCell()}
                        {!firstToolRowDone && noKodeCells()}
                        <td rowSpan={2} className="cs-item align-middle">
                          <span className="num">{si + 1}.</span> {s.item_check}
                        </td>
                        {totalBlnFor(s, si)}
                        <td className="cs-shift" colSpan={2}>QTY SHIFT 1</td>
                        {dayList.map((d) => (
                          <td key={d} className="cs-daycol">
                            <input
                              className="cs-input"
                              value={values[s.id]?.shift1?.[d] ?? ""}
                              onChange={(e) =>
                                setShiftCell(s.id, "shift1", d, e.target.value)
                              }
                              readOnly={!canEditDaily}
                            />
                          </td>
                        ))}
                        <td className="cs-daycol cs-total">
                          {dayList.reduce(
                            (a, d) =>
                              a + (parseFloat(values[s.id]?.shift1?.[d]) || 0),
                            0,
                          ) || ""}
                        </td>
                      </tr>,
                    );
                    // baris 2: SHIFT 2
                    rows.push(
                      <tr key={`${s.id}-s2`}>
                        <td className="cs-shift" colSpan={2}>QTY SHIFT 2</td>
                        {dayList.map((d) => (
                          <td key={d} className="cs-daycol">
                            <input
                              className="cs-input"
                              value={values[s.id]?.shift2?.[d] ?? ""}
                              onChange={(e) =>
                                setShiftCell(s.id, "shift2", d, e.target.value)
                              }
                              readOnly={!canEditDaily}
                            />
                          </td>
                        ))}
                        <td className="cs-daycol cs-total">
                          {dayList.reduce(
                            (a, d) =>
                              a + (parseFloat(values[s.id]?.shift2?.[d]) || 0),
                            0,
                          ) || ""}
                        </td>
                      </tr>,
                    );
                    firstToolRowDone = true;
                  } else if (s.type === "QTY1" || s.type === "QTY2") {
                    // QTY shift terpisah: 1 baris 1 shift, nilai harian langsung
                    const shiftLabel =
                      s.type === "QTY1" ? "QTY SHIFT 1" : "QTY SHIFT 2";
                    rows.push(
                      <tr key={s.id}>
                        {!firstToolRowDone && toolFotoCell()}
                        {!firstToolRowDone && noKodeCells()}
                        <td className="cs-item">
                          <span className="num">{si + 1}.</span> {s.item_check}
                        </td>
                        {totalBlnFor(s, si)}
                        <td className="cs-shift" colSpan={2}>{shiftLabel}</td>
                        {dayList.map((d) => (
                          <td key={d} className="cs-daycol">
                            <input
                              className="cs-input"
                              value={values[s.id]?.[d] ?? ""}
                              onChange={(e) => setCell(s.id, d, e.target.value)}
                              readOnly={!canEditDaily}
                            />
                          </td>
                        ))}
                        <td className="cs-daycol cs-total">
                          {dayList.reduce(
                            (a, d) => a + (parseFloat(values[s.id]?.[d]) || 0),
                            0,
                          ) || ""}
                        </td>
                      </tr>,
                    );
                    firstToolRowDone = true;
                  } else if (s.type === "STROKE") {
                    // baris AKUMULATIF:
                    // akumulatif = (nilai awal QTY / Total QTY bln sblm) + Σ QTY harian berjalan.
                    // Nilai "Standar Stroke" = PATOKAN (tidak ditambahkan).
                    const qtyPrevSum = stds
                      .filter((x) => isQtyType(x.type))
                      .reduce(
                        (a, x) => a + (parseFloat(values[x.id]?.prev) || 0),
                        0,
                      );
                    const akum = computeToolAkum(qtyPrevSum);
                    // patokan standar stroke; akumulatif mencapai/melebihi -> merah
                    const strokeStd = parseFloat(values[s.id]?.prev) || 0;
                    rows.push(
                      <tr key={s.id} className="cs-akum">
                        {!firstToolRowDone && toolFotoCell()}
                        {!firstToolRowDone && noKodeCells()}
                        <td className="cs-item">
                          <span className="num">{si + 1}.</span> {s.item_check}
                        </td>
                        {totalBlnFor(s, si)}
                        <td className="cs-akum-label" colSpan={2}>AKUMULATIF</td>
                        {dayList.map((d) => {
                          const over = strokeStd > 0 && akum[d] >= strokeStd;
                          return (
                            <td
                              key={d}
                              className={`cs-akum-cell${
                                over ? " cs-akum-over" : ""
                              }`}
                            >
                              <span className="cs-akum-val">
                                {akum[d] ? akum[d] : ""}
                              </span>
                            </td>
                          );
                        })}
                        <td className="cs-akum-cell cs-total">
                          {akum[dayList[dayList.length - 1]] || ""}
                        </td>
                      </tr>,
                    );
                    firstToolRowDone = true;
                  } else if (s.type === "NUMBER") {
                    // NUMBER: input angka bebas per hari (1 baris, TIDAK diakumulasi)
                    rows.push(
                      <tr key={s.id}>
                        {!firstToolRowDone && toolFotoCell()}
                        {!firstToolRowDone && noKodeCells()}
                        <td className="cs-item">
                          <span className="num">{si + 1}.</span> {s.item_check}
                        </td>
                        {totalBlnFor(s, si)}
                        <td className="text-center cs-hmb" colSpan={2}>{s.frequency || "-"}</td>
                        {dayList.map((d) => (
                          <td key={d} className="cs-daycol">
                            <input
                              type="number"
                              className="cs-input"
                              value={values[s.id]?.[d] ?? ""}
                              readOnly={!canEditDaily}
                              onChange={(e) => setCell(s.id, d, e.target.value)}
                            />
                          </td>
                        ))}
                        <td className="cs-daycol cs-total">
                          {dayList.reduce(
                            (a, d) => a + (parseFloat(values[s.id]?.[d]) || 0),
                            0,
                          ) || ""}
                        </td>
                      </tr>,
                    );
                    firstToolRowDone = true;
                  } else {
                    // BIASA: sel harian = dropdown (√ / X / L / /)
                    rows.push(
                      <tr key={s.id}>
                        {!firstToolRowDone && toolFotoCell()}
                        {!firstToolRowDone && noKodeCells()}
                        <td className="cs-item">
                          <span className="num">{si + 1}.</span> {s.item_check}
                        </td>
                        {totalBlnFor(s, si)}
                        <td className="text-center cs-hmb" colSpan={2}>{s.frequency || "-"}</td>
                        {dayList.map((d) => {
                          const raw = values[s.id]?.[d] ?? "";
                          const cur = raw === "v" ? "√" : raw; // kompat data lama
                          return (
                            <td key={d} className="cs-daycol">
                              <select
                                className="cs-input cs-sel"
                                value={cur}
                                disabled={!canEditDaily}
                                onChange={(e) =>
                                  setCell(s.id, d, e.target.value)
                                }
                              >
                                <option value=""></option>
                                <option value="√">√</option>
                                <option value="X">X</option>
                                <option value="L">L</option>
                                <option value="/">/</option>
                              </select>
                            </td>
                          );
                        })}
                        <td className="cs-daycol cs-total"></td>
                      </tr>,
                    );
                    firstToolRowDone = true;
                  }
                });

                return (
                  <tbody className="cs-tool-group" key={tool.machine_tool_id}>
                    {rows}
                  </tbody>
                );
              })}
            {/* ===== FOOTER (pemeriksa + catatan + TTD). Saat PRINT, salinannya
                disisipkan otomatis tiap 3 tool (lihat handlePrintSheet) agar tiap
                halaman A4 jadi laporan utuh. ===== */}
            <tbody className="cs-report-foot cs-pem">
              <tr>
                <td className="cs-ket-cell" colSpan={3} rowSpan={2}>
                  <div className="cs-ket-title">Keterangan</div>
                  <div className="cs-ket-legend">
                    <span>&#8730; = Normal</span>
                    <span>L = Pinjam Line Lain</span>
                    <span>X = Bermasalah</span>
                    <span>/ = Libur</span>
                  </div>
                </td>
                {/* Pemeriksa menyerap kolom STANDAR + TOTAL BLN (colSpan 2).
                    H/M & peran berada DI DALAM kolom H/M/B (yang kini dipecah 2):
                    "H" DIGABUNG (rowSpan 3) utk Operator/Leader/MTC di sub-kolom
                    kiri, nama peran di sub-kolom kanan. "M" utk SPV. */}
                <td className="cs-pemeriksa" colSpan={2} rowSpan={4}>
                  <span>Pemeriksa</span>
                </td>
                <td className="cs-hm" rowSpan={3}>
                  H
                </td>
                <td className="cs-role">
                  <span className="cs-role-txt">Operator</span>
                </td>
                {signDayCells("operator")}
              </tr>
              <tr>
                <td className="cs-role">
                  <span className="cs-role-txt">Leader</span>
                </td>
                {signDayCells("leader")}
              </tr>
              <tr>
                <td className="cs-ket-qty-cell" colSpan={3}>
                  QTY = Di isi sesuai QTY proses
                </td>
                <td className="cs-role">
                  <span className="cs-role-txt">MTC</span>
                </td>
                {signDayCells("mtc")}
              </tr>
              <tr>
                <td className="cs-ket-star-cell" colSpan={3}>
                  * Pemeriksaan setiap hari Jumat
                </td>
                <td className="cs-hm">M</td>
                <td className="cs-role">
                  <span className="cs-role-txt">SPV</span>
                </td>
                {signDayCells("spv")}
              </tr>
              {/* Baris Permasalahan / Penyelesaian = SATU sel penuh berisi 2
                  bagian flex masing-masing 50% lebar (bukan dibagi per jumlah
                  kolom, supaya lebarnya SAMA walau kolom kiri lebih lebar dari
                  kolom tanggal). Pemisah = border tengah. */}
              <tr className="cs-masalah-row">
                <td className="cs-masalah-cell" colSpan={8 + dayList.length}>
                  <div className="cs-masalah-split">
                    <div className="cs-masalah-half">
                      <div className="cs-foot-box-title">
                        Permasalahan di Dies / Moulding :
                      </div>
                      <div className="cs-foot-lines" />
                    </div>
                    <div className="cs-masalah-half cs-masalah-half-right">
                      <div className="cs-foot-box-title">
                        Penyelesaian Permasalahan :
                      </div>
                      <div className="cs-foot-lines" />
                    </div>
                  </div>
                </td>
              </tr>
              {/* ===== FOOTER FM-3005 — notes + catatan + No Dok (TANPA border) ===== */}
              <tr className="cs-foot-row">
                <td className="cs-foot-cell" colSpan={8 + dayList.length}>
          <div className="cs-foot">
            <div className="cs-foot-notes">
              <b>Hal-hal yang perlu diperhatikan :</b>
              <ol>
                <li>Kondisi visual jig OK.</li>
                <li>Jika meminjam atau dipinjam, kembalikan.</li>
                <li>
                  Hasil produk dari jig / dies &amp; moulding tersebut tidak
                  sesuai standar.
                </li>
                <li>
                  Setiap selesai memakai Jig, Dies, atau Moulding harap ditulis
                  jumlah QTY pemakaiannya; jumlah akumulatif di akhir bulan
                  dipindahkan ke form check sheet baru bulan berikutnya.
                </li>
                <li>
                  Jika Dies / Moulding tidak normal (pecah, aus) segera
                  dilaporkan kepada Leader Divisi terkait / Maintenance.
                </li>
              </ol>
            </div>

            <div className="cs-foot-catatan">
              <b>CATATAN !</b>
              <div>
                Pengecekan Jig, Dies, &amp; Mould akan tertunda apabila QTY
                pemakaiannya tidak ditulis di kolom yang sudah disediakan.
              </div>
            </div>

            {/* Baris paling bawah: kotak TTD persetujuan (POJOK KANAN BAWAH).
                No Dok & Revisi dipindah KE LUAR tabel/border (lihat .cs-docmeta
                di bawah </table>). */}
            <div className="cs-doc-foot">
              <div className="cs-sign-group">
                <div className="cs-sign">
                  {["Dibuat", "Diperiksa", "Disetujui", "Diketahui"].map(
                    (lbl) => (
                      <div className="cs-sign-box" key={lbl}>
                        <div className="cs-sign-label">{lbl}</div>
                        <div className="cs-sign-space" />
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
                </td>
              </tr>
            </tbody>
            {/* No Dok & Revisi = baris BORDERLESS tersendiri DI BAWAH footer.
                Berada di luar frame grid (sel tanpa border, di bawah baris
                Permasalahan). Saat PRINT, baris ini di-clone & diberi page-break
                sehingga MENUTUP tiap halaman → No Dok di dasar SETIAP halaman,
                di LUAR border (lihat handlePrintSheet). */}
            <tbody className="cs-docmeta-foot">
              <tr>
                <td className="cs-docmeta-cell" colSpan={8 + dayList.length}>
                  <div className="cs-docmeta">
                    <span className="cs-doc-no">No Dok : FM-3005-1</span>
                    <span className="cs-doc-rev">
                      Revisi 03 (tgl 28-05-2024)
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          </div>
          </div>
        </div>
      )}

      {!loading && tools.length > 0 && (
        <div className="d-flex justify-content-end my-3">
          <Button variant="success" onClick={handleSave} disabled={saving}>
            {saving ? "Menyimpan..." : "Simpan"}
          </Button>
        </div>
      )}

      {/* Modal gambar tanda tangan digital */}
      <Modal show={sigModal.open} onHide={closeSig} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Tanda Tangan — {SIGN_ROLES[sigModal.role] || ""}
            {sigModal.day ? ` · Tgl ${sigModal.day}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sigModal.open && (
            <SignaturePad value={sigDraft} onChange={setSigDraft} />
          )}
          <small className="text-muted">
            Gambar tanda tangan di area atas, lalu klik "Terapkan". TTD baru
            benar-benar tersimpan setelah kamu klik tombol <b>Simpan</b> di
            lembar check sheet.
          </small>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeSig}>
            Batal
          </Button>
          <Button variant="primary" onClick={applySig}>
            Terapkan
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ChecksheetTab;
